/**
 * @fileoverview Provide controlled filters over the client-contact reminder review set. `MatterContactReminders.js` owns loading and explicit sends; the backend owns qualifying-communication rules. Local narrowing affects presentation without changing when a matter becomes eligible or delivering a reminder.
 */

import React, { useMemo, useState } from 'react';
import { MultiSelect } from '../shared/AppMultiSelect';
import Button from "../shared/AppButton";
import Chip from '../shared/AppChip';
import { InputText } from '../shared/AppTextInput';
import AppIcon from "../shared/AppIcon";




function MatterContactReminderFilters({
  filters,
  onFilterChange,
  onClearAll,
  filteredCount,
  totalCount,
  stageOptions,
  statusOptions,
  attorneyOptions,
  recipientOptions,
  isLoading
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const activeFilters = useMemo(

  () => {
    const active = [];
    if (filters.search) {
      active.push({ key: 'search', label: `"${filters.search}"`, icon: 'pi pi-search' });
    }
    if (filters.status.length > 0) {
      active.push({ key: 'status', label: filters.status.join(', '), icon: 'pi pi-flag' });
    }
    if (filters.stage.length > 0) {
      active.push({ key: 'stage', label: filters.stage.join(', '), icon: 'pi pi-sitemap' });
    }
    if (filters.attorney.length > 0) {
      active.push({ key: 'attorney', label: filters.attorney.join(', '), icon: 'pi pi-user' });
    }
    if (filters.recipient.length > 0) {
      active.push({ key: 'recipient', label: filters.recipient.join(', '), icon: 'pi pi-send' });
    }
    return active;
  }, [filters]);

  const hasActiveFilters = activeFilters.length > 0;

  const handleRemoveFilter =

  (filterKey) => {
    if (filterKey === 'search') {
      onFilterChange('search', '');
      return;
    }
    onFilterChange(filterKey, []);
  };

  return (
    <div className="filters-wrapper">
            <div className="filters-header">
                <div className="filters-title-section">
                    <AppIcon icon="pi pi-filter filters-icon" />
                    <h2 className="filters-title">Filters</h2>
                    {hasActiveFilters &&
          <span className="filter-count-badge">{activeFilters.length}</span>}

                </div>

                <div className="filters-actions">
                    <span className="filter-results-count">
                        Showing {filteredCount} of {totalCount} offending matters
                    </span>

                    {hasActiveFilters &&
          <Button
            label="Clear All"
            icon="pi pi-times"
            className="p-button-text p-button-sm clear-filters-btn-header"
            onClick={onClearAll} />}



                    <Button
            icon={isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
            className="p-button-text p-button-sm filters-toggle-btn"
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'} />

                </div>
            </div>

            {isExpanded &&
      <div className="filters-content">
                    {hasActiveFilters &&
        <div className="active-filters-chips">
                            {activeFilters.map((filter) =>
          <Chip
            key={filter.key}
            label={filter.label}
            icon={filter.icon}
            removable
            onRemove={() => handleRemoveFilter(filter.key)}
            className="filter-chip" />

          )}
                        </div>}


                    <div className="filters-grid">
                        <div className="filter-group" data-filter-active={Boolean(filters.search)}>
                            <label htmlFor="matter-contact-search-filter" className="filter-label">
                                <AppIcon icon="pi pi-search filter-label-icon" />
                                SEARCH
                            </label>
                            <InputText
              id="matter-contact-search-filter"
              value={filters.search}
              onChange={(e) => onFilterChange('search', e.target.value)}
              placeholder="Matter, client, attorney..."
              disabled={isLoading}
              className={filters.search ? 'filter-active' : ''} />

                        </div>

                        <div className="filter-group" data-filter-active={filters.status.length > 0}>
                            <label htmlFor="matter-contact-status-filter" className="filter-label">
                                <AppIcon icon="pi pi-flag filter-label-icon" />
                                STATUS
                            </label>
                            <MultiSelect
              id="matter-contact-status-filter"
              value={filters.status}
              options={statusOptions}
              onChange={(e) => onFilterChange('status', e.value)}
              placeholder="All Statuses"
              disabled={isLoading}
              className={filters.status.length > 0 ? 'filter-active' : ''}
              maxSelectedLabels={2}
              showClear />

                        </div>

                        <div className="filter-group" data-filter-active={filters.stage.length > 0}>
                            <label htmlFor="matter-contact-stage-filter" className="filter-label">
                                <AppIcon icon="pi pi-sitemap filter-label-icon" />
                                STAGE
                            </label>
                            <MultiSelect
              id="matter-contact-stage-filter"
              value={filters.stage}
              options={stageOptions}
              onChange={(e) => onFilterChange('stage', e.value)}
              filter
              placeholder="All Stages"
              disabled={isLoading}
              className={filters.stage.length > 0 ? 'filter-active' : ''}
              maxSelectedLabels={2}
              showClear />

                        </div>

                        <div className="filter-group" data-filter-active={filters.attorney.length > 0}>
                            <label htmlFor="matter-contact-attorney-filter" className="filter-label">
                                <AppIcon icon="pi pi-user filter-label-icon" />
                                ATTORNEY
                            </label>
                            <MultiSelect
              id="matter-contact-attorney-filter"
              value={filters.attorney}
              options={attorneyOptions}
              onChange={(e) => onFilterChange('attorney', e.value)}
              filter
              placeholder="All Attorneys"
              disabled={isLoading}
              className={filters.attorney.length > 0 ? 'filter-active' : ''}
              maxSelectedLabels={2}
              showClear />

                        </div>

                        <div className="filter-group" data-filter-active={filters.recipient.length > 0}>
                            <label htmlFor="matter-contact-recipient-filter" className="filter-label">
                                <AppIcon icon="pi pi-send filter-label-icon" />
                                RECIPIENT
                            </label>
                            <MultiSelect
              id="matter-contact-recipient-filter"
              value={filters.recipient}
              options={recipientOptions}
              onChange={(e) => onFilterChange('recipient', e.value)}
              placeholder="All Recipient States"
              disabled={isLoading}
              className={filters.recipient.length > 0 ? 'filter-active' : ''}
              maxSelectedLabels={2}
              showClear />

                        </div>
                    </div>
                </div>}

        </div>);

}

export default MatterContactReminderFilters;
